import React, { useContext, useEffect, useState } from 'react';
import { Block } from '../../../../../../../types/shared/blocksV3';
import {
	Box,
	Button,
	Stack,
	styled,
	TextField,
	Typography,
	FormControlLabel,
	Radio,
	RadioGroup,
} from '@mui/material';
import FeetInchesInput from '../../FeetInchesInputV2';
import { DeckContext } from '../../../context/DeckContextV3';
import { toastError } from '../../../../../../../Toaster/Toast';
import {
	MAX_STAIR_HEIGHT,
	MIN_STAIR_WIDTH,
	MIN_STAIR_HEIGHT,
} from '../../../../../../../constants/limits';
import EdgeLabel from '../../../../../../../enums/edgeLabel';
import {
	DEPTH_COEFFICIENT,
	STEPS_COEFFICIENT,
} from '../../../../../../../constants/coefficients';
import updateChildBlocks from '../../../utils/updateChildBlocks';
import getParentBlock from 'src/utils/getParentBlockV3';
import { getVacantSegmentWithoutValidation } from '../../../utils/getVacantSegment';
import { useQuoteState } from 'src/stateProviders/QuoteStateProvider';

const StyledLabel = styled(Typography)({
	fontWeight: 500,
	fontSize: 14,
});

const StairModal: React.FC<{
	block: Block;
	blockData?: Block;
	setBlockData?: (block: Block) => void;
}> = ({
	block,
	blockData: externalBlockData,
	setBlockData: externalSetBlockData,
}) => {
	const { state } = useContext(DeckContext);
	const quoteState = useQuoteState();

	const [internalBlockData, setInternalBlockData] = useState<Block>(block);
	const blockData = externalBlockData || internalBlockData;
	const setBlockData = externalSetBlockData || setInternalBlockData;
	const { parentBlock } = getParentBlock(state.blocks, block.parentBlock);
	const [reinitialize, setReinitialize] = useState(0);

	// Reset internal state when block changes
	useEffect(() => {
		setInternalBlockData(block);
	}, [block.id]);

	const blockAttachedEdge = block.edges.find((ed) => ed.attachedToParent);

	const displayWidthDimension =
		blockAttachedEdge?.label == 'Top' || blockAttachedEdge?.label == 'Bottom'
			? 'width'
			: 'depth';

	const parentEdge = parentBlock.edges.find(
		(edge) => edge.id == block.parentBlock.edgeId
	);

	let segmentAttachedTo;
	let segmentIndex;

	if (parentEdge) {
		segmentIndex =
			parentEdge.segments?.findIndex(
				(segment) => segment.id == block.parentBlock.segmentId
			) ?? -1;

		segmentAttachedTo = parentEdge.segments?.[segmentIndex];
	}

	useEffect(() => {
		setBlockData((prev: Block) => ({
			...prev,
			dimensions: {
				...prev.dimensions,
				endHeight: (prev.dimensions.startHeight || 0) - prev.dimensions.height,
			},
			displayDimensions: {
				width:
					displayWidthDimension == 'width'
						? prev.dimensions.width
						: blockData.dimensions.depth,
				height:
					displayWidthDimension == 'width'
						? blockData.dimensions.depth
						: prev.dimensions.width,
			},
		}));
	}, [
		blockData.dimensions.height,
		blockData.dimensions.depth,
		blockData.dimensions.width,
	]);

	const handleWidthChange = (inches) => {
		if (inches === null || inches === undefined) return;

		if (inches < MIN_STAIR_WIDTH) {
			toastError({
				title: 'Width',
				description: `${block.label} width cannot be less than ${MIN_STAIR_WIDTH / 12} ft`,
			});

			inches = MIN_STAIR_WIDTH;
			setReinitialize(Date.now());
		}

		if (segmentAttachedTo && inches > segmentAttachedTo.length && parentEdge) {
			const vacantSegment = getVacantSegmentWithoutValidation({
				blocks: state.blocks,
				selectedBlockEdge: parentEdge,
			});

			const segmentsLength = parentEdge.segments.reduce(
				(acc, segment) => acc + segment.length,
				0
			);

			const availableLength =
				(vacantSegment?.length || 0) + (parentEdge.length - segmentsLength);

			if (availableLength < inches - segmentAttachedTo.length) {
				toastError({
					title: 'Width',
					description: `Edge ${parentEdge?.label} has no space to expand  ${block.label} to ${inches / 12}ft `,
				});

				inches = blockData.dimensions.width;
				setReinitialize(Date.now());
			}
		}

		setBlockData({
			...blockData,
			dimensions: {
				...blockData.dimensions,
				width: inches,
			},
			displayDimensions: {
				width:
					displayWidthDimension == 'width'
						? inches
						: blockData.dimensions.depth,
				height:
					displayWidthDimension == 'width'
						? blockData.dimensions.depth
						: inches,
			},
			edges: blockData.edges.map((edge) => {
				if (displayWidthDimension == 'width') {
					if (edge.label == EdgeLabel.TOP || edge.label == EdgeLabel.BOTTOM) {
						return {
							...edge,
							length: inches,
						};
					}
					return edge;
				} else {
					if (edge.label == EdgeLabel.LEFT || edge.label == EdgeLabel.RIGHT) {
						return {
							...edge,
							length: inches,
						};
					}

					return edge;
				}
			}),
		});
	};

	const updateDepthEdges = (
		edges: typeof blockData.edges,
		newDepth: number
	) => {
		const attachedEdge = edges.find((e) => e.attachedToParent);
		if (!attachedEdge) return edges;
		const oppositeLabel: Record<string, string> = {
			Top: 'Bottom',
			Bottom: 'Top',
			Left: 'Right',
			Right: 'Left',
		};
		const oppLabel = oppositeLabel[attachedEdge.label as string];
		return edges.map((edge) => {
			if (edge.label === attachedEdge.label || edge.label === oppLabel)
				return edge;
			return {
				...edge,
				length: newDepth,
				segments: edge.segments.map((s) => ({ ...s, length: newDepth })),
			};
		});
	};

	const handleDepthChange = (inches) => {
		if (
			inches === null ||
			inches === undefined ||
			inches == blockData.dimensions.depth
		) {
			console.log('ignore');
			return;
		}

		const numberOfSteps = Math.ceil(inches / DEPTH_COEFFICIENT);
		const height = Math.ceil(numberOfSteps * STEPS_COEFFICIENT);

		setBlockData({
			...blockData,
			dimensions: {
				...blockData.dimensions,
				numberOfSteps,
				height,
				depth: inches,
			},
			edges: updateDepthEdges(blockData.edges, inches),
		});
	};

	const handleHeightChange = (inches) => {
		if (
			inches === null ||
			inches === undefined ||
			inches == blockData.dimensions.height
		) {
			console.log('ignore');
			return;
		}

		if (inches < MIN_STAIR_HEIGHT) {
			toastError({
				title: 'Height',
				description: `${block.label} height cannot be less than ${MIN_STAIR_HEIGHT / 12} ft`,
			});
			inches = MIN_STAIR_HEIGHT;
			setReinitialize(Date.now());
		}

		const numberOfSteps = Math.ceil(inches / STEPS_COEFFICIENT);
		const depth = Math.ceil(numberOfSteps * DEPTH_COEFFICIENT);
		setBlockData({
			...blockData,
			dimensions: {
				...blockData.dimensions,
				height: inches,
				numberOfSteps,
				depth,
			},
			edges: updateDepthEdges(blockData.edges, depth),
		});
	};

	const handleNumberOfStepsChange = (e) => {
		let num;
		try {
			num =
				e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value) || 0);
		} catch (error) {
			console.log(error);
			num = 0;
		}

		if (num == blockData.dimensions.numberOfSteps) {
			console.log('ignore');

			return;
		}

		const maxSteps = Math.min(
			Math.ceil(parentBlock.dimensions.height / STEPS_COEFFICIENT),
			Math.ceil(MAX_STAIR_HEIGHT / STEPS_COEFFICIENT)
		);

		if (num < 2) {
			toastError({
				title: 'Steps',
				description: `${block.label} number of steps cannot be less than 2`,
			});
			num = 2;
		}

		if (num > maxSteps) {
			toastError({
				title: 'Steps',
				description: `${block.label} number of steps cannot be higher than ${maxSteps}`,
			});
			num = maxSteps;
		}

		const height = Math.min(
			Math.ceil(num * STEPS_COEFFICIENT),
			MAX_STAIR_HEIGHT
		);
		const depth = Math.ceil(num * DEPTH_COEFFICIENT);

		const startHeight =
			blockData.dimensions.startHeight || blockData.dimensions.height || 0;
		const endHeight =
			(blockData.dimensions.endHeight || 0) > 0
				? startHeight - height
				: blockData.dimensions.endHeight;

		setBlockData({
			...blockData,
			dimensions: {
				...blockData.dimensions,
				numberOfSteps: num,
				height,
				depth,
				endHeight,
			},
			edges: updateDepthEdges(blockData.edges, depth),
		});
	};

	return (
		<Stack spacing={1.25} sx={{ mt: 2 }}>
			{quoteState.reface && (
				<RadioGroup
					value={blockData.reface ? 'Reface' : 'Rebuild'}
					onChange={(e) => {
						setBlockData({
							...blockData,
							reface: e.target.value == 'Reface',
						});
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'start',
						}}
					>
						<FormControlLabel
							value={'Reface'}
							control={<Radio />}
							label={<StyledLabel>Reface</StyledLabel>}
						/>
						<FormControlLabel
							value={'Rebuild'}
							control={<Radio />}
							label={<StyledLabel>Rebuild</StyledLabel>}
						/>
					</Box>
				</RadioGroup>
			)}

			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: '1fr 3fr',
					alignItems: 'center',
				}}
			>
				<StyledLabel sx={{ marginRight: '10px' }}>Steps</StyledLabel>

				<TextField
					value={blockData.dimensions.numberOfSteps || ''}
					variant="outlined"
					size="small"
					fullWidth
					onChange={handleNumberOfStepsChange}
					type="number"
					slotProps={{
						htmlInput: {
							min: 0,
							max: Math.ceil(MAX_STAIR_HEIGHT / STEPS_COEFFICIENT),
						},
					}}
				/>

				<Box sx={{ flexGrow: 1 }} />
			</Box>

			<FeetInchesInput
				valueInches={blockData.dimensions.depth || 0}
				labelInches="Run Length"
				onChange={handleDepthChange}
				toastLabel="Stair Run Length"
			/>

			<FeetInchesInput
				valueInches={blockData.dimensions.width}
				labelInches="Width"
				onChange={handleWidthChange}
				reinitialize={reinitialize}
				toastLabel="Stair Width"
			/>

			<FeetInchesInput
				valueInches={blockData.dimensions.height}
				labelInches="Height"
				onChange={handleHeightChange}
				reinitialize={reinitialize}
				toastLabel="Stair Height"
			/>
		</Stack>
	);
};

export default StairModal;
